import { useEffect } from "react";
import { Link } from "react-router-dom";
import { UserPlus, Download, Phone, Mail, QrCode } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { FadeIn } from "@/components/Reveal";

const BusinessCard = () => {
  usePageMeta(
    "Virtual Business Card | Holistic Healing Psychotherapy, Inc.",
    "Save the practice contact to your phone in one scan — Holistic Healing Psychotherapy, Inc., Breeza Y. Quezada, LCSW."
  );

  useEffect(() => {
    const tag = document.createElement("meta");
    tag.setAttribute("name", "robots");
    tag.setAttribute("content", "noindex");
    document.head.appendChild(tag);
    return () => document.head.removeChild(tag);
  }, []);

  return (
    <div data-testid="business-card-page">
      <section className="bg-forest-950 pt-16 pb-20 lg:pt-24 lg:pb-24 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-gold-500/8 blur-3xl pointer-events-none" />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 relative">
          <FadeIn>
            <p className="eyebrow mb-6" data-testid="card-eyebrow">Virtual Business Card</p>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.1] text-cream-50" data-testid="card-headline">
              Share my practice in <em className="text-gold-400">one scan</em>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-cream-50/70 leading-relaxed max-w-2xl" data-testid="card-subcopy">
              Point any phone camera at the code to save my contact — name, license, phone, email and office —
              straight to the phone's address book. No app needed.
            </p>
          </FadeIn>

          <FadeIn delay={0.15}>
            <div className="mt-12 rounded-2xl border border-gold-500/35 overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.45)]" data-testid="card-image-frame">
              <img
                src="/business-card/virtual-business-card.png"
                alt="Virtual business card for Holistic Healing Psychotherapy, Inc. with a QR code that saves the practice contact"
                className="w-full h-auto block"
                data-testid="card-image"
              />
            </div>
          </FadeIn>

          <FadeIn delay={0.25}>
            <div className="mt-10 flex flex-wrap items-center gap-4" data-testid="card-actions">
              <a
                href="/business-card/holistic-healing-psychotherapy.vcf"
                download
                data-testid="card-save-contact"
                className="inline-flex items-center gap-2 rounded-full bg-cream-50 text-forest-900 font-semibold text-sm px-7 py-4 transition-colors duration-300 hover:bg-white"
              >
                <UserPlus size={16} /> Save Contact
              </a>
              <a
                href="/business-card/virtual-business-card.png"
                download
                data-testid="card-download-image"
                className="inline-flex items-center gap-2 rounded-full border border-gold-500/50 text-gold-300 font-semibold text-sm px-7 py-4 transition-colors duration-300 hover:border-gold-400 hover:text-gold-400"
              >
                <Download size={16} /> Download Card
              </a>
              <a
                href="tel:+16573339466"
                data-testid="card-call"
                className="inline-flex items-center gap-2 rounded-full border border-cream-50/20 text-cream-50/85 font-semibold text-sm px-7 py-4 transition-colors duration-300 hover:border-gold-500 hover:text-gold-400"
              >
                <Phone size={16} /> (657) 333-9466
              </a>
              <a
                href="mailto:bqholistichealing@gmail.com"
                data-testid="card-email"
                className="inline-flex items-center gap-2 rounded-full border border-cream-50/20 text-cream-50/85 font-semibold text-sm px-7 py-4 transition-colors duration-300 hover:border-gold-500 hover:text-gold-400"
              >
                <Mail size={16} /> Email Me
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="bg-cream-50 py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <FadeIn>
            <p className="eyebrow mb-4 flex items-center gap-2"><QrCode size={15} className="text-gold-500" /> The Codes, On Their Own</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-forest-800 tracking-tight" data-testid="card-qr-heading">
              For print, signatures, and sharing.
            </h2>
          </FadeIn>
          <div className="mt-10 grid sm:grid-cols-2 gap-6">
            {[
              {
                src: "/business-card/qr-contact.png",
                title: "Save My Contact",
                copy: "Adds the practice to the phone's contacts — name, title, phone, email, website and office address.",
                testid: "card-qr-contact",
              },
              {
                src: "/business-card/qr-website.png",
                title: "Visit the Website",
                copy: "Opens hhpsychotherapy.com — services, fees, and the consultation request form.",
                testid: "card-qr-website",
              },
            ].map((q, i) => (
              <FadeIn key={q.testid} delay={0.1 + i * 0.08}>
                <div className="h-full rounded-2xl border border-forest-800/10 bg-white/70 p-8 text-center" data-testid={q.testid}>
                  <img src={q.src} alt={`${q.title} QR code`} className="w-56 h-56 mx-auto rounded-xl" />
                  <h3 className="font-serif text-2xl text-forest-800 mt-6">{q.title}</h3>
                  <p className="text-sm text-sage leading-relaxed mt-2">{q.copy}</p>
                  <a
                    href={q.src}
                    download
                    data-testid={`${q.testid}-download`}
                    className="inline-flex items-center gap-2 mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-gold-700 hover:text-gold-600 transition-colors"
                  >
                    <Download size={13} /> Download PNG
                  </a>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.2}>
            <p className="mt-10 text-sm text-sage text-center" data-testid="card-back-note">
              Ready to talk?{" "}
              <Link to="/contact" className="text-gold-700 underline underline-offset-4 hover:text-gold-600" data-testid="card-contact-link">
                Schedule a free 20-minute consultation
              </Link>
              .
            </p>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default BusinessCard;
